import { useTranslation } from "react-i18next";
import { Plus, Trash2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useStackTags } from "@/hooks/useStackTags";
import type { ProviderFormState, ServiceLocation } from "./types";
import { REGIONS, SERVICE_CATEGORIES } from "./types";

const inputClass =
  "w-full h-10 px-3 border border-border bg-background text-sm focus:outline-none focus:border-primary transition-colors";
const selectClass = inputClass;
const labelClass = "text-micro block mb-2";

const RADIUS_OPTIONS = [10, 25, 50, 75, 100, 150, 200];

interface Props {
  form: ProviderFormState;
  update: (field: string, value: any) => void;
  toggleArrayItem: (field: string, item: string) => void;
}

function generateId() {
  return crypto.randomUUID();
}

export default function TabService({ form, update, toggleArrayItem }: Props) {
  const { t } = useTranslation();
  const { groupedByCategory, isLoading: tagsLoading } = useStackTags();

  const addLocation = () => {
    const loc: ServiceLocation = { id: generateId(), city: "", postal_code: "", radius_km: 50 };
    update("service_locations", [...form.service_locations, loc]);
  };

  const removeLocation = (id: string) => {
    update("service_locations", form.service_locations.filter((l) => l.id !== id));
  };

  const updateLocation = (id: string, field: keyof ServiceLocation, value: any) => {
    update(
      "service_locations",
      form.service_locations.map((l) => (l.id === id ? { ...l, [field]: value } : l))
    );
  };

  return (
    <div className="space-y-8">
      <section className="border border-border bg-card p-6 space-y-6">
        <h3 className="text-subheadline">{t("opService.regionsTitle")}</h3>
        <div>
          <label className={labelClass}>{t("opService.mainRegion")}</label>
          <select value={form.region} onChange={(e) => update("region", e.target.value)} className={selectClass}>
            <option value="">{t("opService.pleaseSelect")}</option>
            {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>{t("opService.serviceRegions")}</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {REGIONS.map((r) => (
              <button key={r} type="button" onClick={() => toggleArrayItem("service_regions", r)}
                className={cn("px-3 py-1.5 text-xs border transition-colors",
                  form.service_regions.includes(r) ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                )}>
                {r}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className={labelClass + " !mb-0"}>{t("opService.serviceLocations")}</label>
              <p className="text-caption">{t("opService.serviceLocationsDesc")}</p>
            </div>
            <Button type="button" variant="quiet" size="sm" onClick={addLocation}>
              <Plus className="w-4 h-4 mr-1" />
              {t("opService.addLocation")}
            </Button>
          </div>

          {form.service_locations.length === 0 && (
            <div className="p-6 border border-dashed border-border text-center">
              <MapPin className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
              <p className="text-caption">{t("opService.noLocations")}</p>
            </div>
          )}

          {form.service_locations.map((loc, idx) => (
            <div key={loc.id} className="border border-border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-micro">{t("opService.locationN", { n: idx + 1 })}</span>
                <button type="button" onClick={() => removeLocation(loc.id)} className="p-1 text-muted-foreground hover:text-destructive transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={labelClass}>{t("opService.locationCity")}</label>
                  <input value={loc.city} onChange={(e) => updateLocation(loc.id, "city", e.target.value)} placeholder={t("opService.locationCityPlaceholder")} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>{t("opService.locationPostal")}</label>
                  <input value={loc.postal_code} onChange={(e) => updateLocation(loc.id, "postal_code", e.target.value)} placeholder={t("opService.locationPostalPlaceholder")} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>{t("opService.locationRadius")}</label>
                  <select value={loc.radius_km} onChange={(e) => updateLocation(loc.id, "radius_km", Number(e.target.value))} className={selectClass}>
                    {RADIUS_OPTIONS.map((r) => (
                      <option key={r} value={r}>{r} km</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>

        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={form.remote_supported} onChange={(e) => update("remote_supported", e.target.checked)} className="w-4 h-4 accent-accent" />
          <span className="text-sm">{t("opService.remoteSupport")}</span>
        </label>
      </section>

      <section className="border border-border bg-card p-6 space-y-6">
        <h3 className="text-subheadline">{t("opService.serviceCategories")}</h3>
        <div className="flex flex-wrap gap-2">
          {SERVICE_CATEGORIES.map((cat) => (
            <button key={cat.value} type="button" onClick={() => toggleArrayItem("categories", cat.value)}
              className={cn("px-4 py-2 text-sm border transition-colors",
                form.categories.includes(cat.value) ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
              )}>
              {cat.label}
            </button>
          ))}
        </div>
      </section>

      <section className="border border-border bg-card p-6 space-y-6">
        <h3 className="text-subheadline">{t("opService.expertiseTitle")}</h3>
        {tagsLoading ? (
          <p className="text-caption">{t("opService.tagsLoading")}</p>
        ) : (
          Object.entries(groupedByCategory).map(([category, tags]) => (
            <div key={category}>
              <label className={labelClass}>{category}</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {tags.map((tag) => (
                  <button key={tag.id} type="button" onClick={() => toggleArrayItem("selectedStackTags", tag.id)}
                    className={cn("px-3 py-1.5 text-xs border transition-colors",
                      form.selectedStackTags.includes(tag.id) ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30",
                      tag.is_advanced && "italic"
                    )}>
                    {tag.name}{tag.is_advanced && " ⚡"}
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </section>

      <section className="border border-border bg-card p-6 space-y-6">
        <h3 className="text-subheadline">{t("opService.slaTitle")}</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>{t("opService.slaTier")}</label>
            <select value={form.sla_tier} onChange={(e) => update("sla_tier", e.target.value)} className={selectClass}>
              <option value="2H">{t("opService.sla2h")}</option>
              <option value="4H">{t("opService.sla4h")}</option>
              <option value="NBD">{t("opService.slaNBD")}</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>{t("opService.slaResponseTime")}</label>
            <input type="number" value={form.sla_response_hours} onChange={(e) => update("sla_response_hours", Number(e.target.value))} min={1} max={72} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>{t("opService.weeklyLimit")}</label>
            <input type="number" value={form.weekly_lead_limit} onChange={(e) => update("weekly_lead_limit", Number(e.target.value))} min={1} max={50} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>{t("opService.monthlyLimit")}</label>
            <input type="number" value={form.monthly_opportunity_limit} onChange={(e) => update("monthly_opportunity_limit", Number(e.target.value))} min={1} max={200} className={inputClass} />
          </div>
        </div>
      </section>

      <section className="border border-border bg-card p-6 space-y-6">
        <h3 className="text-subheadline">{t("opService.qualityTitle")}</h3>
        <p className="text-caption">{t("opService.qualityDesc")}</p>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>{t("opService.qualityScore")}</label>
            <input type="number" value={form.quality_score} onChange={(e) => update("quality_score", parseFloat(e.target.value))} step={0.05} min={0} max={1} className={inputClass} />
            <p className="text-micro mt-1">{t("opService.qualityScoreRange")}</p>
          </div>
          <div>
            <label className={labelClass}>{t("opService.acceptanceRate")}</label>
            <input type="number" value={form.acceptance_rate} onChange={(e) => update("acceptance_rate", parseFloat(e.target.value))} step={0.05} min={0} max={1} className={inputClass} />
            <p className="text-micro mt-1">{t("opService.qualityScoreRange")}</p>
          </div>
          <div>
            <label className={labelClass}>{t("opService.avgResponseTime")}</label>
            <input type="number" value={form.avg_response_time_hours} onChange={(e) => update("avg_response_time_hours", Number(e.target.value))} min={1} max={72} className={inputClass} />
          </div>
        </div>
      </section>
    </div>
  );
}
